/* eslint-disable jsx-a11y/anchor-is-valid */
import { isEmpty, map, extend } from 'underscore';
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Panel as BootstrapPanel } from 'react-bootstrap';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  Collapse,
  TextTitle,
  ValidatedBootstrapPanel,
  ValidatedFixedPanel,
  ValidatedPanel,
  getPanelErrorProps,
  useCollapseState,
  collapseExpander,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { Link, Stack } from '@bingads-webui-react/fabric-control';
import { Campaign } from '@bingads-webui/campaign-odata-schemas';
import { ValidationGroup, ErrorMessages } from '@bingads-webui-react/validation';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { FormErrorGroup, UnmappedFormErrors, andComp, entityIs, propertyIs, codeIs, anyComp } from '@bingads-webui-campaign-react/form-error';
import { useFormService } from '@bingads-webui-react/form';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { RemainingCharsValidatedTextField } from '@bingads-webui-campaign-react/remaining-chars-hint';
import {
  ValidatedLanguageTarget, LanguageTargetValidatorFactory, campaignLanguageStrings,
  ValidatedAutoGeneratedAssets, getAutoGeneratedAssetsDetails,
  ValidatedConversionGoals,
  ValidatedDisclaimer,
  PredictiveMatching, getPredictiveMatchingDetails,
  ValidatedBidAdjustment, getBidAdjuestmentTitleDetails, getBidAdjuestmentTitleSummary,
  ValidatedLocationTargetSetting, getLocationTargetTitleDetails,
  ValidatedCampaignDynamicSearchAdSetting,
  ValidatedBrandLists, getBrandListsTitleDetails,
} from '@bingads-webui-campaign-react/search-campaign-wizard-controls';
import { WebExclusionsSection } from '@bingads-webui-react/web-exclusions';
import { MultimediaAdBidAdjustmentSection } from '@bingads-webui-campaign-react/multimedia-ad-adjustment';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { loadBundleCountries, ERROR_ENTITY } from '@bingads-webui-campaign-react/search-campaign-data';
import {
  getConversionGoalTitleDetails,
  getDisclaimerTitleDetails,
} from '@bingads-webui-campaign-react/wizard-shared-components';
import { ValidatedLegacyBidStrategy } from '@bingads-webui-campaign/bid-strategy';
import { ValidatedLegacyAdSchedule, getAdScheduleDetails } from '@bingads-webui-campaign/ad-schedule';
import { getBidStrategyTitleDetails, bidStratetyTypeValue, ScopeLevel } from '@bingads-webui-campaign-react/constants';
import { ValidatedLegacySharedBudget, getBudgetTitleDetails } from '@bingads-webui-campaign/shared-budget';
import { testUurl, uurlLevels } from '@bingads-webui-component-legacy/upgraded-url/service/testUurl';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { URLOptionsSection } from '@bingads-webui-campaign-react/validated-url-input';
import {
  ValidatedCompanyPicker,
  ValidatedIndustryPicker,
  ValidatedJobFunctionPicker,
} from '@bingads-webui-campaign-react/display-plus-campaigns';
import { getMessageType } from '@bingads-webui-campaign-react/campaign-settings-utils';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';

import { getStyles } from './styles';
import DisclaimerImg from '../../shared//media/disclaimer.svg';
import LanguageImg from '../../shared//media/language.svg';
import AutoAssetsImg from '../../shared//media/auto-assets.svg';
import ConversionGoalsImg from '../../shared/media/conversion-goals.svg';
import AdScheduleImg from '../../shared/media/ad-schedule.svg';
import PredictiveMatchingImg from '../../shared/media/predictive-matching.svg';
import BidStrategyImg from '../../shared/media/bid-strategy.svg';
import BudgetImg from '../../shared/media/budget.svg';
import DeviceImg from '../../shared/media/device.svg';
import GenderImg from '../../shared/media/gender.svg';
import AgeImg from '../../shared/media/age.svg';
import BrandListsImg from '../../shared/media/brand-lists.svg';
import LocationImg from '../../shared//media/location.svg';
import CompanyNameImg from '../../shared/media/company-name.svg';
import IndustryImg from '../../shared/media/industry.svg';
import JobFunctionImg from '../../shared/media/job-function.svg';
import { SEARCH_WIZARD_TYPE_NAME } from '../../wizards/constants';

const entityIsCampaign = entityIs(ERROR_ENTITY.Campaign);
const campaignNameProp = andComp(entityIsCampaign, propertyIs('Name'));
const campaignLanguagesProp = andComp(entityIsCampaign, propertyIs('Languages'));
const campaignConversionProp = andComp(entityIs(ERROR_ENTITY.CampaignConversionGoal), propertyIs('CampaignConversionGoalAssociations'));
const autoGenerateAssetsEnabledProp = andComp(entityIsCampaign, propertyIs('AutoGeneratedAssetsEnabled'));
const dsaSettingsProp = andComp(entityIsCampaign, codeIs('DynamicSearchAdsSettingNotAllowedForUpdate'));
const disclaimerProp = anyComp(
  entityIs(ERROR_ENTITY.CampaignDisclaimerAdExtension),
  propertyIs('DisclaimerAdsEnabled')
);
const campaignLocationCriterions = anyComp(
  entityIs(ERROR_ENTITY.CampaignLocation),
  entityIs(ERROR_ENTITY.CampaignRadius),
  entityIs(ERROR_ENTITY.CampaignNegativeLocation),
  entityIs(ERROR_ENTITY.CampaignLocationIntent)
);

const ServerValidatedLanguageTarget = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLanguageTarget, { filter: campaignLanguagesProp }));
const ServerValidatedAutoGeneratedAssets = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedAutoGeneratedAssets, { filter: autoGenerateAssetsEnabledProp }));
const ServerValidatedCampaignDynamicSearchAdSetting = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedCampaignDynamicSearchAdSetting, { filter: dsaSettingsProp }));
const ServerValidatedDisclaimer = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedDisclaimer, { filter: disclaimerProp }));
const ServerValidatedConversionGoals = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedConversionGoals, { filter: campaignConversionProp }));
const ServerValidatedLegacyAdSchedule = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLegacyAdSchedule, { filter: entityIs(ERROR_ENTITY.CampaignDayTimeCriterion) }));
const ServerValidatedLegacyBidStrategy = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLegacyBidStrategy, { filter: andComp(entityIsCampaign, propertyIs('BiddingScheme')) }));
const ServerValidatedLegacySharedBudget = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLegacySharedBudget, { filter: andComp(entityIsCampaign, propertyIs('Budget')) }));
const ServerValidatedDevice = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedBidAdjustment, { filter: entityIs(ERROR_ENTITY.CampaignDeviceOSCriterion) }));
const ServerValidatedGender = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedBidAdjustment, { filter: entityIs(ERROR_ENTITY.CampaignGenderCriterion) }));
const ServerValidatedAge = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedBidAdjustment, { filter: entityIs(ERROR_ENTITY.CampaignAgeCriterion) }));
const ServerValidatedBrandLists = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedBrandLists, { filter: entityIs(ERROR_ENTITY.BrandListAssociation) }));
const ServerValidatedLocationTargetSetting = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLocationTargetSetting, { filter: campaignLocationCriterions }));
const ServerValidatedCompanyName = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedCompanyPicker, { filter: entityIs(ERROR_ENTITY.CampaignCompanyNameCriterion) }));
const ServerValidatedIndustry = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedIndustryPicker, { filter: entityIs(ERROR_ENTITY.CampaignIndustryCriterion) }));
const ServerValidatedJobFunction = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedJobFunctionPicker, { filter: entityIs(ERROR_ENTITY.CampaignJobFunctionCriterion) }));

export const CampaignEditSettingsStep = ({
  data,
  onDataChange,
  error,
}) => {
  const {
    campaign,
  } = data || {};

  const {
    serverErrors = [],
    systemErrors = [],
  } = error || {};

  const i18n = useContext(I18nContext);
  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);
  const { scope } = useContext(ScopeContext);
  const activity = useContext(ActivityContext);
  const { config: wizardConfig, state, send } = useWizardService();
  const wizardData = useMemo(() => state.context.wizardData || {}, [state.context.wizardData]);

  const {
    cid, aid, campaignId, uid,
  } = scope.values;
  const classes = useDefaultStyles(getStyles);

  const serverContext = useContext(ServerContext);
  const {
    CurrentAccount: {AdvertiserCustomerID: advertiserCustomerId },
    CurrentLocale: locale,
    CurrentUser: { Market: market },
    DynamicPermissions,
    Permissions,
    Token,
  } = serverContext || {};
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);
  const {
    ConversionAssociationWarning: IsConversionAssociationWarningEnabled,
    ConversionAssociationWarningP2: IsConversionAssociationWarningP2Enabled,
  } = permissions.dynamic || {};
  const { IsDSAMixedModeCampaignEnabled } = permissions || {};

  const isExperiment = !!(campaign && campaign.ExperimentId);
  const isMixedMode = IsDSAMixedModeCampaignEnabled && !isExperiment;

  const { state: { context: { saveFailed: showAllValidationErrors } } } = useFormService();
  const [adScheduleView, setAdScheduleView] = useState(null);

  const {
    Languages, TrackingUrlTemplate, FinalUrlSuffix, UrlCustomParameters,
  } = campaign || {};
  const selectedLanguages = useRef(Languages);
  selectedLanguages.current = Languages;

  const refreshLocationTargetsTimes = useRef(0);

  const urlOptions = useMemo(() => ({
    TrackingUrlTemplate,
    FinalUrlSuffix,
    UrlCustomParameters,
  }), [FinalUrlSuffix, TrackingUrlTemplate, UrlCustomParameters]);

  const urlOptionsDetails = useRef(urlOptions);
  urlOptionsDetails.current = urlOptions;

  const [
    allCampaignSettingsSections,
    campaignSettingsExpandedSections,
    setCampaignSettingsExpandedSections,
    flipCampaignSettingsExpandAllState,
  ] = useCollapseState(
    ['campaign', 'languages', 'budget', 'bid-strategy', 'auto-generate-assets'] /* all sections */,
    ['campaign', 'languages', 'budget', 'bid-strategy', 'auto-generate-assets'] /* initially expanded sections */
  );

  const basicSettingsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setCampaignSettingsExpandedSections }),
    [setCampaignSettingsExpandedSections]
  );

  const onValidate = useCallback((result) => {
    if (data && data.campaign) {
      const newLanguages = result && result.data && result.data.Languages;
      const patchData = {
        ...data,
        campaign: {
          ...data.campaign,
          ...result.data,
        },
      };
      if (IsConversionAssociationWarningP2Enabled && wizardConfig && wizardConfig.id === SEARCH_WIZARD_TYPE_NAME.SEARCH_CAMPAIGN_EDIT_WIZARD) {
        const message = getMessageType(data, patchData);
        if (message) {
          const updatedData = {
            wizardData: {
              ...wizardData,
              customConfirmMessages: [message],
            },
          };
          send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, updatedData.wizardData);
        }
      }

      if (!isEmpty(newLanguages) && selectedLanguages.current !== newLanguages) {
        selectedLanguages.current = newLanguages;
        refreshLocationTargetsTimes.current = (refreshLocationTargetsTimes.current + 1) % 1000;
        loadBundleCountries({
          cid, aid, serverContext, language: newLanguages,
        }).then((bundleCountriesAndLocationCriterions) => {
          onDataChange({
            data: {
              ...patchData,
              ...bundleCountriesAndLocationCriterions,
            },
            isValid: result.isValid,
          });
        });
      } else {
        onDataChange({
          data: {
            ...patchData,
          },
          isValid: result.isValid,
        });
      }
    }
  }, [IsConversionAssociationWarningP2Enabled, aid, cid, data, onDataChange, send, serverContext, wizardConfig, wizardData]);

  const webExclusionsError = serverErrors.filter(err => err.property === 'webExclusions');
  const setExclusionsCallback = useCallback((exclusionsData, type) => {
    if (data) {
      const patchedData = {
        data: {
          ...data,
          exclusions: {
            ...(data.exclusions || {}),
            campaignExclusions: {
              ...(data.exclusions && data.exclusions.campaignExclusions ? data.exclusions.campaignExclusions : {}),
              [type]: exclusionsData,
            },
          },
        },
      };
      onDataChange(patchedData);
    }
  }, [data, onDataChange]);

  const basicSettingsSection = useMemo(() => {
    const campaignPanel = (
      <ValidatedFixedPanel
        key="campaign"
        title={<TextTitle titleKey={_TL_('Campaign name')} topicKey="pop_BA_CampaignCreate_Settings_CampaignName" />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <RemainingCharsValidatedTextField
          placeholder={i18n.getString(_TL_('Enter a name for your campaign'))}
          id="input-campaign-name"
          validationProps={{
            field: 'Name',
            schema: Campaign,
          }}
          i18n={i18n}
          formErrorProps={{ filter: campaignNameProp }}
        />
      </ValidatedFixedPanel>
    );

    const languagesPanel = (
      <ValidatedPanel
        key="languages"
        title={<TextTitle
          titleKey={_TL_('Languages')}
          topicKey="pop_BA_CampaignCreate_Settings_LanguageTargeting"
          details={campaign && map(campaign.Languages, lang => i18n.getString(campaignLanguageStrings[lang] || _TL_(`${lang}`))).join(', ')}
          icon={LanguageImg}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        actuationProps={{
          expand: basicSettingsExpand,
          expandKey: 'languages',
        }}
      >
        <ServerValidatedLanguageTarget
          locale={locale}
          permissions={permissions}
          validationProps={{
            field: 'Languages',
            validator: LanguageTargetValidatorFactory(i18n),
          }}
        />
      </ValidatedPanel>
    );

    const budgetPanel = (
      <ValidatedPanel
        key="budget"
        noOverflowHidden
        title={<TextTitle titleKey={_TL_('Budget')} topicKey="pop_BA_CampaignCreate_Budget_SharedBudget" icon={BudgetImg} details={getBudgetTitleDetails(serverContext, i18n, data && data.campaign && data.campaign.Budget)} />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedLegacySharedBudget
          className="vanila-budget"
          campaignId={data && data.campaign && data.campaign.Id}
          readOnly={isExperiment}
          validationProps={{
            data: { Budget: data && data.campaign && data.campaign.Budget },
            onValidate,
            schema: Campaign,
            field: 'Budget',
         }}
        />
      </ValidatedPanel>
    );

    const biddingScheme = data && data.campaign && data.campaign.BiddingScheme;
    const bidType = biddingScheme && biddingScheme.Type;
    const bidStrategy = biddingScheme && biddingScheme.BidStrategyId;
    let bidOption = bidType;
    if (!bidOption || bidStrategy) {
      bidOption = bidStratetyTypeValue.portfolioBidStrategy;
    }
    const bidStrategyPanel = (
      <ValidatedPanel
        key="bid-strategy"
        noOverflowHidden
        title={<TextTitle titleKey={_TL_('Bid strategy')} topicKey="pop_BA_CampaignGrids_BidStrategy" icon={BidStrategyImg} details={getBidStrategyTitleDetails(i18n, bidOption)} />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedLegacyBidStrategy
          entityType="Campaign"
          hideHeader
          id="bid-strategy-search-campaign"
          campaignType="search"
          i18nNewDesign={i18n}
          i18n={i18n}
          isCreation
          isInitialize
          needConfirmManagePortfolio
          autoTargetingEnabled={data && data.campaign && data.campaign.AutoTargetingEnabled}
          budgetAmount={data && data.campaign && data.campaign.Budget && data.campaign.Budget.Amount}
          bidStrategyName={data && data.campaign && data.campaign.BidStrategyName}
          validationProps={{
            data: { BiddingScheme: biddingScheme && isEmpty(biddingScheme) ? campaign.Portofolio : biddingScheme },
            schema: Campaign,
            field: 'BiddingScheme',
            onValidate,
            stopPropagation: false,
          }}
          activityLogger={activity}
        />
      </ValidatedPanel>
    );

    const autoGenerateAssetsPanel = (
      <ValidatedPanel
        key="auto-generate-assets"
        noOverflowHidden
        title={<TextTitle
          titleKey={_TL_('Autogenerated assets')}
          icon={AutoAssetsImg}
          topicKey="pop_BA_RSA_AutoAssets"
          details={getAutoGeneratedAssetsDetails(i18n, campaign && campaign.AutoGeneratedAssetsEnabled)}
          isRecommended
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedAutoGeneratedAssets
          validationProps={{
            field: 'AutoGeneratedAssetsEnabled',
            schema: Campaign,
          }}
        />
      </ValidatedPanel>
    );

    const basicSettings = [
      campaignPanel,
      languagesPanel,
      budgetPanel,
      data && data.campaign && bidStrategyPanel,
      autoGenerateAssetsPanel,
    ];

    return (
      <div className={classNames('basic-campaign-section')}>
        <Stack horizontalAlign="end" tokens={{ padding: '9px 0 0 0' }} className="jumbo-card-control-all-btn-stack">
          <Link className="jumbo-card-control-all-btn" onClick={flipCampaignSettingsExpandAllState}>{campaignSettingsExpandedSections.length ? i18n.getString(_TL_('Collapse all')) : i18n.getString(_TL_('Expand all'))}</Link>
        </Stack>
        <Collapse allPanelItems={allCampaignSettingsSections} checkItems skipItemsTypeCheck activeKeys={campaignSettingsExpandedSections} setActiveKeys={setCampaignSettingsExpandedSections}>
          { basicSettings }
        </Collapse>
      </div>
    );
  }, [activity, allCampaignSettingsSections, basicSettingsExpand, campaign, campaignSettingsExpandedSections, data, flipCampaignSettingsExpandAllState, i18n, locale, onValidate, permissions, serverContext, setCampaignSettingsExpandedSections]);


  const [
    allBidAdjustmentSections,
    bidAdjustmentExpandedSections,
    setBidAdjustmentExpandedSections,
    flipBidAdjustmentExpandAllState,
    expandAllBidAdjustmentSections,
  ] = useCollapseState(
    ['locations', 'device', 'gender', 'age', 'companyName', 'industry', 'jobFunction'] /* all sections */,
    [] /* initially expanded sections */
  );

  const allBidAdjustmentExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setBidAdjustmentExpandedSections }),
    [setBidAdjustmentExpandedSections]
  );

  const [bidAdjustmentSectionExpanded, setBidAdjustmentSectionExpanded] = useState(false);
  const onBidAdjustmentSectionToggle = useCallback((expanded) => {
    setBidAdjustmentSectionExpanded(expanded);
  }, []);

  const bidAdjustmentSection = useMemo(() => {
    const locationPanel = (
      <ValidatedPanel
        key="locations"
        noOverflowHidden
        title={<TextTitle
          titleKey={_TL_('Location')}
          topicKey="pop_BA_CampaignCreate_Settings_LocationTargeting"
          details={
            getLocationTargetTitleDetails(
              i18n,
              campaign && campaign.locationTargets,
              data && data.bundleCountries,
              false,
              campaign && campaign.BiddingScheme && campaign.BiddingScheme.Type,
              true
            )}
          icon={LocationImg}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        actuationProps={{
          expand: basicSettingsExpand,
          expandKey: 'locations',
        }}
      >
        <ServerValidatedLocationTargetSetting
          validationProps={{
            data: campaign && campaign.locationTargets,
          }}
          bundleCountries={data && data.bundleCountries}
          bundleLocationCriterions={data && data.bundleLocationCriterions}
          refreshLocationTargetsTimes={refreshLocationTargetsTimes.current}
          language={Languages}
          market={market}
          permissions={permissions}
          scope={scope}
          bidStrategyType={campaign && campaign.DisplayBiddingStrategyType}
          allowBidAdjustment
        />
      </ValidatedPanel>
    );
    const devicePanel = (
      campaign && campaign.Device &&
      <ValidatedPanel
        key="device"
        noOverflowHidden
        title={<TextTitle
          titleKey={_TL_('Device')}
          icon={DeviceImg}
          topicKey="pop_BA_CampaignCreate_AudTargets_Device"
          details={getBidAdjuestmentTitleDetails(i18n, permissions.IsOriginalDeviceTargetingEnabled ? [] : campaign && campaign.Device, 'Device')}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedDevice
          cid={cid}
          aid={aid}
          campaignId={campaignId}
          criterionType="Device"
          criterionLevel="CampaignCriterion"
          level="campaign"
          i18n={i18n}
          data={campaign && campaign.Device}
        />
      </ValidatedPanel>
    );

    const genderPanel = (
      campaign && campaign.Gender &&
      <ValidatedPanel
        key="gender"
        noOverflowHidden
        title={<TextTitle
          titleKey={_TL_('Gender')}
          icon={GenderImg}
          topicKey="pop_BA_CampaignCreate_AudTargets_Gender"
          details={getBidAdjuestmentTitleDetails(i18n, campaign && campaign.Gender, 'Gender')}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedGender
          cid={cid}
          aid={aid}
          campaignId={campaignId}
          criterionType="Gender"
          criterionLevel="CampaignCriterion"
          level="campaign"
          i18n={i18n}
          data={campaign && campaign.Gender}
        />
      </ValidatedPanel>
    );

    const agePanel = (
      campaign && campaign.Age &&
      <ValidatedPanel
        key="age"
        noOverflowHidden
        title={<TextTitle
          titleKey={_TL_('Age')}
          icon={AgeImg}
          topicKey="pop_BA_CampaignCreate_AudTargets_Age"
          details={getBidAdjuestmentTitleDetails(i18n, campaign && campaign.Age, 'Age')}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedAge
          cid={cid}
          aid={aid}
          campaignId={campaignId}
          criterionType="Age"
          criterionLevel="CampaignCriterion"
          level="campaign"
          i18n={i18n}
          data={campaign && campaign.Age}
        />
      </ValidatedPanel>
    );

    // For campaign under Pilot 1183 DomesticSimplifiedChinese, companyName, industry, jobFunction is not applicable
    const companyNamePanel = permissions && !permissions.IsDomesticSimplifiedChineseEnabled && campaign && (
      <ValidatedPanel
        key="companyName"
        noOverflowHidden
        title={<TextTitle
          titleKey={_TL_('Company')}
          icon={CompanyNameImg}
          topicKey="pop_BA_CampaignCreate_AudTargets_Company"
          details={getBidAdjuestmentTitleSummary(i18n, campaign.companyNameTargets, {
            singleTarget: _TL_('Targeted: {{number}} company'),
            multipleTargets: _TL_('Targeted: {{number}} companies'),
          })}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedCompanyName
          classes={classes}
          showBidAdjustments
          showExclusion={false}
          validationProps={{
            data: {
              companyNameTargets: campaign.companyNameTargets,
            },
          }}
        />
      </ValidatedPanel>
    );

    const industryPanel = permissions && !permissions.IsDomesticSimplifiedChineseEnabled && campaign && (
      <ValidatedPanel
        key="industry"
        noOverflowHidden
        title={<TextTitle
          titleKey={_TL_('Industry')}
          icon={IndustryImg}
          topicKey="pop_BA_CampaignCreate_AudTargets_Industry"
          details={getBidAdjuestmentTitleSummary(i18n, campaign.industry, {
            singleTarget: _TL_('Targeted: {{number}} industry'),
            multipleTargets: _TL_('Targeted: {{number}} industries'),
          })}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedIndustry
          classes={classes}
          showBidAdjustments
          showExclusion={false}
          initialIndustryTargets={campaign.initialIndustry}
          validationProps={{
            data: {
              industry: campaign.industry,
            },
          }}
        />
      </ValidatedPanel>
    );

    const jobFunctionPanel = permissions && !permissions.IsDomesticSimplifiedChineseEnabled && campaign && (
      <ValidatedPanel
        key="jobFunction"
        noOverflowHidden
        title={<TextTitle
          titleKey={_TL_('Job function')}
          icon={JobFunctionImg}
          topicKey="pop_BA_CampaignCreate_AudTargets_JobFunction"
          details={getBidAdjuestmentTitleSummary(i18n, campaign.jobTargets, {
            singleTarget: _TL_('Targeted: {{number}} job'),
            multipleTargets: _TL_('Targeted: {{number}} jobs'),
          })}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedJobFunction
          classes={classes}
          showBidAdjustments
          showExclusion={false}
          initialJobTargets={campaign.initialJobTargets}
          validationProps={{
            data: {
              jobTargets: campaign.jobTargets,
            },
          }}
        />
      </ValidatedPanel>
    );

    const bidAdjustment = [
      locationPanel,
      agePanel,
      devicePanel,
      genderPanel,
      companyNamePanel,
      industryPanel,
      jobFunctionPanel,
    ];

    const onBidAdjustmentSectionCollapse = () => {
      if (!bidAdjustmentSectionExpanded) {
        setBidAdjustmentSectionExpanded(true);
        expandAllBidAdjustmentSections();
      } else {
        flipBidAdjustmentExpandAllState();
      }
    };

    return (
      <div className={classNames('bid-adjustment-section', classes.bidAdjustmentSection)}>
        <ValidatedBootstrapPanel
          onToggle={onBidAdjustmentSectionToggle}
          expanded={bidAdjustmentSectionExpanded}
          actuationProps={{
            expand: allBidAdjustmentExpand,
          }}
          className={classNames(classes.collapsiblePanel, 'bid-adjustment-panel')}
          render={() => (
            <>
              <BootstrapPanel.Heading>
                <BootstrapPanel.Title toggle>
                  {i18n.getString(_TL_('Bid adjustments'))}
                </BootstrapPanel.Title>
                <Stack horizontalAlign="end" tokens={{ padding: '9px 0 0 0' }} className="jumbo-card-control-all-btn-stack">
                  <Link className="jumbo-card-control-all-btn" onClick={onBidAdjustmentSectionCollapse}>{bidAdjustmentSectionExpanded && bidAdjustmentExpandedSections.length ? i18n.getString(_TL_('Collapse all')) : i18n.getString(_TL_('Expand all')) }</Link>
                </Stack>
              </BootstrapPanel.Heading>
              <BootstrapPanel.Collapse>
                <BootstrapPanel.Body>
                  <div className={classNames('bid-adjustment-section')}>
                    <Collapse allPanelItems={allBidAdjustmentSections} checkItems skipItemsTypeCheck activeKeys={bidAdjustmentExpandedSections} setActiveKeys={setBidAdjustmentExpandedSections}>
                      { bidAdjustment }
                    </Collapse>
                  </div>
                </BootstrapPanel.Body>
              </BootstrapPanel.Collapse>
            </>
            )}
        />
      </div>
    );
  }, [Languages, aid, allBidAdjustmentExpand, allBidAdjustmentSections, basicSettingsExpand, bidAdjustmentExpandedSections, bidAdjustmentSectionExpanded, campaign, campaignId, cid, classes, data, expandAllBidAdjustmentSections, flipBidAdjustmentExpandAllState, i18n, market, onBidAdjustmentSectionToggle, permissions, scope, setBidAdjustmentExpandedSections]);

  const [
    allAdvancedSettingsSections,
    advancedSettingsExpandedSections,
    setAdvancedSettingsExpandedSections,
    flipAdvancedSettingsExpandAllState,
    expandAllAdvancedSettingsSections,
  ] = useCollapseState(
    ['conversions', 'disclaimer', 'web-exclusions', 'dsa-settings', 'ad-schedule', 'advanced-url-options', 'predictive-matching', 'brand-lists', 'multimedia-ad'] /* all sections */,
    ['conversions', 'disclaimer', 'dsa-settings', 'ad-schedule', 'web-exclusions', 'predictive-matching', 'brand-lists'] /* initially expanded sections */
  );

  const allAdvancedSettingsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setAdvancedSettingsExpandedSections }),
    [setAdvancedSettingsExpandedSections]
  );

  const [advancedSettingsSectionExpanded, setAdvancedSettingsSectionExpanded] = useState(false);
  const onAdvancedSettingsSectionToggle = useCallback((expanded) => {
    setAdvancedSettingsSectionExpanded(expanded);
  }, []);

  const triggerTestUurl = useCallback((fn, originalView) => {
    extend(originalView, {
      isInline: false,
      level: uurlLevels.Campaign,
    });
    const context = {
      customerId: cid,
      userId: uid,
      accountId: aid,
      campaignId,
    };
    const params = urlOptionsDetails.current;
    const options = {
      apiInLog: 'advancedSettingsUrlOptions/uurlTest',
    };
    testUurl(fn, originalView, params, context, options);
  }, [aid, campaignId, cid, uid]);

  const advancedSettingsSection = useMemo(() => {
    const dsaSettingPanel = (
      <ValidatedPanel
        key="dsa-settings"
        noOverflowHidden
        title={
          <TextTitle
            titleKey={_TL_('Dynamic Search ads')}
            icon={AutoAssetsImg}
            topicKey="pop_BA_DSA_DynamicSearchAds"
            // todo: details
            isRecommended
            details={i18n.getString(_TL_('Dynamic Search ads'))}
          />
        }
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedCampaignDynamicSearchAdSetting
          validationProps={{
            data: data && data.campaign,
          }}
        />
      </ValidatedPanel>
    );

    const supportedBiddingStrategiesForPredictiveMatching = [
      bidStratetyTypeValue.targetCpa,
      bidStratetyTypeValue.targetRoas,
      bidStratetyTypeValue.maximizeConversions,
    ];

    const biddingScheme = data && data.campaign && data.campaign.BiddingScheme.Type;

    const showPredictiveMatching = DynamicPermissions && DynamicPermissions.PredictiveMatching && (supportedBiddingStrategiesForPredictiveMatching.includes(biddingScheme) || DynamicPermissions.PredictiveMatchingPhase2);

    const predictiveMatchingPanel = (
      <ValidatedPanel
        key="predictive-matching"
        noOverflowHidden
        title={
          <TextTitle
            titleKey={_TL_('Predictive matching')}
            icon={PredictiveMatchingImg}
            topicKey="pop_BA_Keywords_PredictiveMatching"
            details={getPredictiveMatchingDetails(i18n, data && data.campaign && (data.campaign.isPredictiveMatchingChecked === undefined ? data.campaign.AutoTargetingEnabled : data.campaign.isPredictiveMatchingChecked))}
            isRecommended
          />
        }
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <PredictiveMatching
          i18n={i18n}
          onValidate={onValidate}
          isPredictiveMatchingChecked={data && data.campaign && data.campaign.isPredictiveMatchingChecked}
          AutoTargetingEnabled={data && data.campaign && data.campaign.AutoTargetingEnabled}
          hideDescription
          disabled={!supportedBiddingStrategiesForPredictiveMatching.includes(biddingScheme)}
        />
      </ValidatedPanel>
    );

    const showBrandLists = DynamicPermissions && DynamicPermissions.PredictiveMatching && DynamicPermissions.PredictiveMatchingPhase2;

    const brandListsPanel = (
      <ValidatedPanel
        key="brand-lists"
        noOverflowHidden
        title={
          <TextTitle
            titleKey={_TL_('Brand safety')}
            icon={BrandListsImg}
            details={getBrandListsTitleDetails(i18n, data && data.campaign && data.campaign.brandProtections)}
            isRecommended
          />
        }
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedBrandLists
          campaignId={campaignId}
          validationProps={{
            initialValue: data && data.campaign,
            data: data && data.campaign,
            onValidate,
          }}
          disabled={!(data && data.campaign && (data.campaign.isPredictiveMatchingChecked === undefined ? data.campaign.AutoTargetingEnabled : data.campaign.isPredictiveMatchingChecked))}
        />
      </ValidatedPanel>
    );

    const adSchedulePanel = (
      <ValidatedPanel
        key="ad-schedule"
        noOverflowHidden
        title={<TextTitle titleKey={_TL_('Ad schedule')} topicKey="pop_BA_Targeting_DayTime" icon={AdScheduleImg} details={`${getAdScheduleDetails(i18n, adScheduleView && adScheduleView.current)}`} />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedLegacyAdSchedule
          entity={{
            data: {
              TimeZone: data && data.campaign && data.campaign.TimeZone,
              AdScheduleUseSearcherTimeZone: data && data.campaign && data.campaign.AdScheduleUseSearcherTimeZone,
              Id: data && data.campaign && data.campaign.Id,
            },
          }}
          validationProps={{
            field: 'AdSchedule',
            onValidate,
            data: {
              dayTimeRanges: data && data.campaign && data.campaign.dayTimeRanges,
            },
          }}
          setAdScheduleView={setAdScheduleView}
          showBidAdjustmentColumn
        />
      </ValidatedPanel>
    );

    const urlOptionPanel = (
      data && data.campaign && <URLOptionsSection
        key="advanced-url-options"
        trackingUrlTemplate={TrackingUrlTemplate}
        finalUrlSuffix={FinalUrlSuffix}
        urlCustomParameters={UrlCustomParameters}
        token={Token}
        triggerTestUurl={triggerTestUurl}
      />
    );
    const conversionsPanel = (
      <ValidatedPanel
        key="conversions"
        noOverflowHidden
        title={<TextTitle
          titleKey={_TL_('Conversions')}
          details={getConversionGoalTitleDetails(i18n, campaign && campaign.conversionGoals)}
          icon={ConversionGoalsImg}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        topicKey="pop_BA_PMAX_Conversions"
      >
        <ServerValidatedConversionGoals
          validationProps={{
            initialValue: data && data.campaign,
            data: data && data.campaign,
            onValidate,
          }}
          cid={advertiserCustomerId}
          aid={aid}
          i18n={i18n}
          showTrackingStatusIcon={IsConversionAssociationWarningEnabled}
        />
      </ValidatedPanel>
    );

    const disclaimerPanel = (
      <ValidatedPanel
        key="disclaimer"
        noOverflowHidden
        title={<TextTitle
          titleKey={_TL_('Disclaimer')}
          details={getDisclaimerTitleDetails(i18n, campaign && campaign.disclaimer)}
          icon={DisclaimerImg}
          topicKey="pop_BA_DisclaimerAd"
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedDisclaimer
          campaignId={campaign && campaign.Id}
          validationProps={{
            data: {
              disclaimerAdsEnabled: campaign && campaign.DisclaimerAdsEnabled,
              disclaimer: campaign && campaign.disclaimer,
            },
          }}
        />
      </ValidatedPanel>
    );

    const websiteExclusionsPanel = (
      <WebExclusionsSection
        key="web-exclusions"
        showSiteExclusion
        showIPExclusion
        level={ScopeLevel.Campaign}
        exclusionData={data && data.exclusions ? data.exclusions : {}}
        setExclusionsCallback={setExclusionsCallback}
        errors={webExclusionsError}
        expand={allAdvancedSettingsExpand}
      />
    );

    const multimediaAdsData = campaign ? {
      multimediaAds: campaign.multimediaAds,
      AutomatedCallToActionOptOut: campaign.AutomatedCallToActionOptOut,
    } : null;
    const multimediaAdPanel = (
      <MultimediaAdBidAdjustmentSection
        key="multimedia-ad"
        level={ScopeLevel.Campaign}
        data={multimediaAdsData}
        onDataChange={onValidate}
        expand={allAdvancedSettingsExpand}
        permissions={permissions}
      />
    );

    const advancedSettings = [
      showPredictiveMatching ? predictiveMatchingPanel : null,
      showBrandLists ? brandListsPanel : null,
      isMixedMode ? dsaSettingPanel : null,
      adSchedulePanel,
      urlOptionPanel,
      conversionsPanel,
      disclaimerPanel,
      multimediaAdPanel,
      websiteExclusionsPanel,
    ];

    const onAdvancedSettingsSectionCollapse = () => {
      if (!advancedSettingsSectionExpanded) {
        setAdvancedSettingsSectionExpanded(true);
        expandAllAdvancedSettingsSections();
      } else {
        flipAdvancedSettingsExpandAllState();
      }
    };

    return (
      <div className={classNames('advanced-campaign-section', classes.advancedCampaignSection)}>
        <ValidatedBootstrapPanel
          onToggle={onAdvancedSettingsSectionToggle}
          expanded={advancedSettingsSectionExpanded}
          actuationProps={{
            expand: allAdvancedSettingsExpand,
          }}
          className={classNames(classes.collapsiblePanel, 'advanced-settings-panel')}
          render={() => (
            <>
              <BootstrapPanel.Heading>
                <BootstrapPanel.Title toggle>
                  {i18n.getString(_TL_('Advanced settings'))}
                </BootstrapPanel.Title>
                <Stack horizontalAlign="end" tokens={{ padding: '9px 0 0 0' }} className="jumbo-card-control-all-btn-stack">
                  <Link className="jumbo-card-control-all-btn" onClick={onAdvancedSettingsSectionCollapse}>{advancedSettingsSectionExpanded && advancedSettingsExpandedSections.length ? i18n.getString(_TL_('Collapse all')) : i18n.getString(_TL_('Expand all'))}</Link>
                </Stack>
              </BootstrapPanel.Heading>
              <BootstrapPanel.Collapse>
                <BootstrapPanel.Body>
                  <div className={classNames('advanced-settings-section')}>
                    <Collapse allPanelItems={allAdvancedSettingsSections} checkItems skipItemsTypeCheck activeKeys={advancedSettingsExpandedSections} setActiveKeys={setAdvancedSettingsExpandedSections}>
                      { advancedSettings }
                    </Collapse>
                  </div>
                </BootstrapPanel.Body>
              </BootstrapPanel.Collapse>
            </>
            )}
        />
      </div>
    );
  }, [DynamicPermissions, permissions, FinalUrlSuffix, IsConversionAssociationWarningEnabled, Token, TrackingUrlTemplate, UrlCustomParameters, adScheduleView, advancedSettingsExpandedSections, advancedSettingsSectionExpanded, aid, allAdvancedSettingsExpand, allAdvancedSettingsSections, campaign, campaignId, cid, classes.advancedCampaignSection, classes.collapsiblePanel, data, expandAllAdvancedSettingsSections, flipAdvancedSettingsExpandAllState, i18n, onAdvancedSettingsSectionToggle, onValidate, setAdvancedSettingsExpandedSections, setExclusionsCallback, triggerTestUurl, webExclusionsError]);

  return (
    <div className={classNames(classes.campaignEditSettingsStep, 'campaign-edit-settings-step')}>
      <ActuationGroup actuationProps={{ isActive: showAllValidationErrors }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors }}>
            <div className="server-errors">
              <UnmappedFormErrors />
            </div>
            <div className="system-errors">
              <ErrorMessages messages={systemErrors} />
            </div>
            <ValidationGroup
              validationProps={{
                schema: Campaign,
                onValidate,
                data: campaign || {},
                i18n: legacyI18n,
              }}
            >
              <div className={classes.formContainer}>
                { basicSettingsSection }
                { bidAdjustmentSection }
                { advancedSettingsSection }
              </div>
            </ValidationGroup>
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </div>
  );
};

CampaignEditSettingsStep.propTypes = {
  data: PropTypes.shape({
    campaign: PropTypes.shape({
      Id: PropTypes.string,
      Name: PropTypes.string,
      AutoTargetingEnabled: PropTypes.bool,
      BiddingScheme: PropTypes.shape({
        Type: PropTypes.string,
      }),
      BidStrategyName: PropTypes.string,
      Budget: PropTypes.shape({
        Amount: PropTypes.number,
      }),
      isPredictiveMatchingChecked: PropTypes.bool,
      brandProtections: PropTypes.shape({}),
      locationTargets: PropTypes.arrayOf(PropTypes.shape({})),
      dayTimeRanges: PropTypes.arrayOf(PropTypes.shape({})),
      TimeZone: PropTypes.string,
      AdScheduleUseSearcherTimeZone: PropTypes.bool,
    }),
    bundleCountries: PropTypes.shape({}),
    bundleLocationCriterions: PropTypes.shape({}),
    websites: PropTypes.shape([]),
    exclusions: PropTypes.shape({
      campaignExclusions: PropTypes.shape({}),
    }).isRequired,
    multimediaAds: PropTypes.shape({}).isRequired,
    dsaSettings: PropTypes.shape({}).isRequired,
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.shape({
    serverErrors: PropTypes.arrayOf(PropTypes.shape({})),
    systemErrors: PropTypes.arrayOf(PropTypes.shape({})),
  }),
};

CampaignEditSettingsStep.defaultProps = {
  error: null,
};
